/**
 * Norse free-shipping threshold — bootstrap, formatting, and copy.
 * Loaded synchronously from snippets/shipping-threshold-boot.liquid.
 *
 * External scripts may set window.__NORSE_FS_SHOP_CENTS and dispatch
 * norse:fs-threshold-update { detail: { shopCents } }.
 */
(function (global) {
  'use strict';

  var PLACEHOLDERS = ['{ shipping_threshold }', '{{ shipping_threshold }}', '{shipping_threshold}'];
  var HARDCODED_SELECTORS = [
    '.rv-benefit__text:not([data-norse-shipping-threshold])',
    '.rv-shipBadge:not([data-norse-shipping-threshold])',
    '.norse-usps-small ul li',
    '.norse-usps-small ol li',
    '.selling-plans-alt__benefit',
    '[data-nstbb-benefits] li',
    /* The quiz results trust row — snippets/norse-quiz-results-usps.liquid. */
    '.nqr-usps__label',
  ];
  // Match Intelligems / product prices (Intl en-US symbols: A$, CA$, NZ$…).
  // Do not clone `.igtb__price-current` — on PDPs that node is often `$110`
  // with the A/CA letter in a sibling, which made cart copy regress to `$72`.
  var DOLLAR_SYMBOL_PREFIX = {
    AUD: 'A$',
    CAD: 'CA$',
    NZD: 'NZ$',
    HKD: 'HK$',
    SGD: 'S$',
    MXN: 'MX$',
  };

  var _originalCopy = new WeakMap();
  var _boot = null;
  var _pendingHoldExpired = false;
  var _moneyFormat;

  var MONEY_SPACE = '\\s\\u00a0\\u202f';

  /**
   * Derive the presentment money format from a sample Liquid rendered with the
   * `money` filter (123456 cents). This is currency- and market-correct by
   * construction — guessing an Intl locale from the currency code is not, and
   * produced "€4.01" where the rest of the page showed "€35,99".
   */
  function parseMoneySample(sample) {
    sample = String(sample || '');
    var m = sample.match(new RegExp('\\d[\\d.,' + MONEY_SPACE + ']*\\d|\\d'));
    if (!m) return null;

    var num = m[0];
    var decimalMatch = num.match(/([.,])(\d{2})$/);
    var thousandsMatch = num.match(new RegExp('\\d([.,' + MONEY_SPACE + '])\\d{3}'));

    return {
      prefix: sample.slice(0, m.index),
      suffix: sample.slice(m.index + num.length),
      decimalSep: decimalMatch ? decimalMatch[1] : '',
      thousandsSep: thousandsMatch ? thousandsMatch[1] : '',
    };
  }

  function moneyFormat() {
    if (_moneyFormat === undefined) {
      var data = activeData() || readBootstrap() || {};
      _moneyFormat = data.moneySample ? parseMoneySample(data.moneySample) : null;
    }
    return _moneyFormat;
  }

  function formatWithSample(cents, fmt) {
    var negative = cents < 0;
    cents = Math.abs(Math.round(cents));

    var whole;
    var fraction = null;
    if (fmt.decimalSep) {
      whole = Math.floor(cents / 100);
      fraction = String(cents % 100);
      while (fraction.length < 2) fraction = '0' + fraction;
    } else {
      whole = Math.round(cents / 100);
    }

    var digits = String(whole);
    if (fmt.thousandsSep) {
      digits = digits.replace(/\B(?=(\d{3})+(?!\d))/g, fmt.thousandsSep);
    }

    var out = fmt.prefix + digits + (fraction === null ? '' : fmt.decimalSep + fraction) + fmt.suffix;
    return negative ? '-' + out : out;
  }

  global.setTimeout(function () {
    _pendingHoldExpired = true;
    run();
  }, 3000);

  function readBootstrapEl() {
    var el = document.getElementById('norse-shipping-threshold-data');
    if (!el) return null;
    try {
      return { el: el, data: JSON.parse(el.textContent) };
    } catch (e) {
      return null;
    }
  }

  function readBootstrap() {
    var parsed = readBootstrapEl();
    return parsed ? parsed.data : null;
  }

  function activeData() {
    if (_boot) return _boot;
    if (global.NorseShippingThreshold && global.NorseShippingThreshold.presentmentCents) {
      return global.NorseShippingThreshold;
    }
    return readBootstrap();
  }

  /**
   * False in a market with no free-shipping rate at all — Mexico sells paid
   * rates only. The server already drops the copy (shipping-threshold-text,
   * progress-bar, the rv-benefit rows), so this exists to stop the client
   * putting it back: a stale norse_fs_shop_cents cookie would otherwise let
   * patchBootstrap derive a threshold and rewrite "$50" into "MX$75".
   */
  function thresholdEnabled() {
    var data = activeData() || readBootstrap();
    return !data || data.enabled !== false;
  }

  /**
   * Drop any free-shipping copy left in the DOM by an AJAX section render.
   * List items collapse via the class the stylesheet already ships; anything
   * else is hidden outright, since a benefit row is icon + copy and the copy
   * is the whole point.
   */
  function hideFreeShippingCopy() {
    var selectors = HARDCODED_SELECTORS.concat(['[data-norse-shipping-threshold]']);
    selectors.forEach(function (selector) {
      document.querySelectorAll(selector).forEach(function (el) {
        if (!isFreeShippingText(el.textContent) && !containsPlaceholder(el.textContent)) return;
        var li = el.closest('li');
        if (li && li.closest('.norse-usps-small')) {
          li.classList.add('norse-usps-small__item--hidden');
          return;
        }
        // Inline style, not `hidden`: `.rv-benefit` is `display: flex` from a
        // block-scoped selector, which outranks the UA's `[hidden]` rule.
        var row = el.closest('.rv-benefit') || li || el;
        row.style.display = 'none';
      });
    });
    document.querySelectorAll('.norse-usps-small--threshold-pending').forEach(function (el) {
      el.classList.remove('norse-usps-small--threshold-pending');
    });
  }

  function stripTrailingCents(str) {
    // Only zero cents are dropped. The previous `/\.\d{2}$/` rule also ate real
    // cents, rendering a €4.01 shortfall as "€4".
    return String(str || '').replace(/[.,]00(?=\s|[A-Z]{3}|$)/, '');
  }

  function roundToDisplayCents(cents) {
    cents = Math.round(Number(cents) || 0);
    return Math.floor((cents + 50) / 100) * 100;
  }

  function displayThresholdCents(data) {
    data = data || activeData() || readBootstrap() || {};
    if (data.displayCents > 0) return data.displayCents;
    if (data.presentmentCents > 0) return roundToDisplayCents(data.presentmentCents);
    return 0;
  }

  function stripRedundantIsoCode(str, currency) {
    if (!str || !currency) return str;
    return String(str).replace(new RegExp('\\s+' + currency + '\\b', 'i'), '');
  }

  function applyDollarSymbolPrefix(formatted, currency) {
    var prefix = DOLLAR_SYMBOL_PREFIX[currency];
    if (!prefix || !formatted) return formatted;
    var out = String(formatted).replace(/^[\s\u00a0\u200e\u200f\ufeff]+/, '');
    if (out.indexOf(prefix) !== -1) return out;
    if (currency === 'CAD' && out.indexOf('C$') === 0) return 'CA$' + out.slice(2);
    if (currency === 'AUD' && out.indexOf('AU$') === 0) return 'A$' + out.slice(3);
    if (out.charAt(0) === '$') return prefix + out.slice(1);
    return out.replace('$', prefix);
  }

  function applyLeiSuffix(formatted, currency) {
    if (currency !== 'RON' || !formatted) return formatted;
    var out = String(formatted)
      .replace(/^[\s\u00a0\u200e\u200f\ufeff]+/, '')
      .replace(/\s+/g, ' ')
      .trim();
    if (/\blei\b/i.test(out) && !/^RON\b/i.test(out)) return out;
    var m = out.match(/^RON\s*(.+)$/i);
    if (m) {
      var rest = m[1].replace(/\s+RON\b/i, '').trim();
      if (/\blei\b/i.test(rest)) return rest;
      return rest + ' lei';
    }
    out = out.replace(/\s+RON\b/i, '').trim();
    if (/\blei\b/i.test(out)) return out;
    return out + ' lei';
  }

  function formatMoney(cents, opts) {
    opts = opts || {};
    cents = Math.round(Number(cents) || 0);
    if (!(cents >= 0)) return '';

    var data = activeData() || readBootstrap() || {};
    var cartCurrency =
      opts.cartCurrency ||
      data.cartCurrency ||
      (global.Shopify && global.Shopify.currency && global.Shopify.currency.active) ||
      'USD';

    var formatted = '';
    var hasFraction = opts.keepCents || cents % 100 !== 0;
    var fmt = moneyFormat();
    if (fmt) {
      formatted = formatWithSample(cents, fmt);
    } else {
      try {
        formatted = new Intl.NumberFormat(cartCurrency === 'RON' ? 'ro-RO' : 'en-US', {
          style: 'currency',
          currency: cartCurrency,
          currencyDisplay: 'symbol',
          minimumFractionDigits: hasFraction ? 2 : 0,
          maximumFractionDigits: hasFraction ? 2 : 0,
        }).format(cents / 100);
      } catch (e) {
        if (global.Shopify && typeof global.Shopify.formatMoney === 'function') {
          formatted = global.Shopify.formatMoney(cents);
        } else {
          formatted = String(cents / 100);
        }
      }
    }

    formatted = applyDollarSymbolPrefix(formatted, cartCurrency);
    formatted = stripRedundantIsoCode(formatted, cartCurrency);
    formatted = opts.keepCents ? formatted : stripTrailingCents(formatted);
    return applyLeiSuffix(formatted, cartCurrency);
  }

  function getPresentmentThresholdCents(shopCents) {
    shopCents = parseInt(shopCents, 10);
    if (!(shopCents > 0)) return 0;

    var data = activeData() || readBootstrap();
    if (!data) return shopCents;

    if (data.shopCurrency === data.cartCurrency) return shopCents;
    if (data.shopCents > 0 && data.presentmentCents > 0) {
      return Math.round(shopCents * (data.presentmentCents / data.shopCents));
    }
    return shopCents;
  }

  function parseCookieShopCents() {
    var m = document.cookie.match(/(?:^|;\s*)norse_fs_shop_cents=(\d+)/);
    return m ? parseInt(m[1], 10) : 0;
  }

  function readShopOverride(metaShopCents) {
    var shop = 0;
    if (global.__NORSE_FS_SHOP_CENTS) shop = parseInt(global.__NORSE_FS_SHOP_CENTS, 10);
    if (!(shop > 0)) {
      try {
        shop = parseInt(sessionStorage.getItem('norse_fs_shop_cents') || '0', 10);
      } catch (e) {}
    }
    if (!(shop > 0)) shop = parseCookieShopCents();
    if (!(shop > 0)) return 0;
    if (metaShopCents > 0 && shop === metaShopCents) return 0;
    return shop;
  }

  function applyShopThresholdOverride(data, shopCents, presentmentAttr, metaShopCents) {
    shopCents = parseInt(shopCents, 10) || 0;
    if (!(shopCents > 0)) return false;
    if (metaShopCents > 0 && shopCents === metaShopCents) return false;
    if (shopCents === data.shopCents && shopCents !== metaShopCents) {
      var presAttr = parseInt(presentmentAttr, 10) || 0;
      if (presAttr > 0 && presAttr !== data.presentmentCents) {
        data.presentmentCents = presAttr;
        data.displayCents = roundToDisplayCents(data.presentmentCents);
        data.formatted = formatMoney(data.displayCents);
        return true;
      }
      return false;
    }
    if (shopCents === data.shopCents) return false;

    var ratio = data.shopCents > 0 ? data.presentmentCents / data.shopCents : 1;
    if (data.shopCurrency === data.cartCurrency) ratio = 1;

    var presAttr = parseInt(presentmentAttr, 10) || 0;
    if (
      presAttr > 0 &&
      data.cartCurrency !== data.shopCurrency &&
      Math.abs(presAttr - Math.round(shopCents * ratio)) <= Math.max(2, Math.round(shopCents * ratio * 0.02))
    ) {
      data.presentmentCents = presAttr;
    } else {
      data.presentmentCents = Math.round(shopCents * ratio);
    }

    data.shopCents = shopCents;
    data.displayCents = roundToDisplayCents(data.presentmentCents);
    global.__NORSE_FS_SHOP_CENTS = String(shopCents);
    persistThreshold(data.shopCents, data.presentmentCents);
    data.formatted = formatMoney(data.displayCents);
    return true;
  }

  function syncFromCartAttributes() {
    if (!thresholdEnabled()) return Promise.resolve(false);
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        var attrs = (cart && cart.attributes) || {};
        var parsed = readBootstrapEl();
        if (!parsed) return false;

        var data = Object.assign({}, parsed.data, activeData() || {});
        var metaShop = parseInt(data.metaShopCents, 10) || 0;
        var changed = applyShopThresholdOverride(
          data,
          attrs._fs_threshold,
          attrs._fs_threshold_presentment,
          metaShop
        );

        if (!changed) return false;
        parsed.el.textContent = JSON.stringify(data);
        publish(data);
        run();
        return true;
      })
      .catch(function () {
        return false;
      });
  }

  function scheduleCartThresholdSync() {
    syncFromCartAttributes();
    global.setTimeout(syncFromCartAttributes, 400);
    global.setTimeout(syncFromCartAttributes, 1200);
    global.setTimeout(syncFromCartAttributes, 2500);
  }

  function persistThreshold(shopCents, presentmentCents) {
    try {
      sessionStorage.setItem('norse_fs_shop_cents', String(shopCents));
      if (presentmentCents > 0) {
        sessionStorage.setItem('norse_fs_presentment_cents', String(presentmentCents));
      }
    } catch (e) {}
    document.cookie = 'norse_fs_shop_cents=' + shopCents + ';path=/;max-age=604800;SameSite=Lax';
    if (presentmentCents > 0) {
      document.cookie =
        'norse_fs_presentment_cents=' + presentmentCents + ';path=/;max-age=604800;SameSite=Lax';
    }
  }

  function isFreeShippingText(text) {
    var t = String(text || '').toLowerCase();
    if (!t.includes('free')) return false;
    return t.includes('ship') || t.includes('delivery') || t.includes('postage');
  }

  function containsPlaceholder(str) {
    if (!str) return false;
    for (var i = 0; i < PLACEHOLDERS.length; i++) {
      if (str.indexOf(PLACEHOLDERS[i]) !== -1) return true;
    }
    return false;
  }

  function looksAlreadyLocalized(text) {
    var t = String(text || '');
    if (/\$\s?\d+(?:\.\d{2})?/.test(t)) return false;
    return /\b(RON|EUR|GBP|CAD|AUD|USD|lei)\b/i.test(t) || /€|£/.test(t);
  }

  function needsClientRewrite(text) {
    if (containsPlaceholder(text)) return true;
    if (/\$\s?\d+(?:\.\d{2})?/.test(text)) return true;
    if (/€\s?\d+(?:[.,]\d{2})?/.test(text)) return true;
    if (/£\s?\d+(?:\.\d{2})?/.test(text)) return true;
    if (looksAlreadyLocalized(text)) return false;
    return /\b(over|above|from)\s+\d+(?:[.,]\d{2})?\b/i.test(String(text || ''));
  }

  function replaceInString(str, money) {
    if (!str || !money) return str;
    var out = str;
    for (var i = 0; i < PLACEHOLDERS.length; i++) {
      out = out.split(PLACEHOLDERS[i]).join(money);
    }
    if (!isFreeShippingText(out) && !needsClientRewrite(out)) return out;

    if (/\$\s?\d+(?:\.\d{2})?/.test(out) || /€\s?\d+(?:[.,]\d{2})?/.test(out) || /£\s?\d+(?:\.\d{2})?/.test(out)) {
      out = out.replace(/\$\s?\d+(?:\.\d{2})?/g, money);
      out = out.replace(/€\s?\d+(?:[.,]\d{2})?/g, money);
      out = out.replace(/£\s?\d+(?:\.\d{2})?/g, money);
    }

    if (!looksAlreadyLocalized(out)) {
      out = out.replace(/\b(over|above|from)\s+(\d+(?:[.,]\d{2})?)\b/gi, function (match, word, num) {
        var n = parseFloat(String(num).replace(',', '.'));
        if (n >= 10 && n <= 999) return word + ' ' + money;
        return match;
      });
    }
    return out;
  }

  function applyToElement(root, money) {
    if (!root || !money) return;
    if (root.closest && root.closest('[data-norse-shipping-threshold]')) return;

    root.querySelectorAll('li, p, span, div').forEach(function (node) {
      if (node.children.length) return;
      var original = (node.textContent || '').trim();
      if (!original) return;
      if (!isFreeShippingText(original) && !needsClientRewrite(original)) return;
      var updated = replaceInString(original, money);
      if (updated !== original) node.textContent = updated;
    });
  }

  function markReady() {
    document.documentElement.classList.add('fs-threshold-ready');
  }

  function hasPendingUsps() {
    return document.querySelectorAll('.norse-usps-small--threshold-pending').length > 0;
  }

  function isTestThresholdActive() {
    var data = activeData() || readBootstrap();
    if (!data) return false;
    var meta = parseInt(data.metaShopCents, 10) || 0;
    var shop = parseInt(data.shopCents, 10) || 0;
    var externalShop = parseInt(global.__NORSE_FS_SHOP_CENTS, 10) || 0;
    if (externalShop > 0 && meta > 0 && externalShop !== meta) return true;
    if (meta > 0 && shop > 0 && shop !== meta) return true;
    return false;
  }

  function canRevealPending() {
    if (!hasPendingUsps()) return true;
    if (isTestThresholdActive()) return true;
    return _pendingHoldExpired;
  }

  function markReadyIfAppropriate() {
    if (canRevealPending()) markReady();
  }

  function publish(data) {
    data.displayCents = roundToDisplayCents(data.displayCents || data.presentmentCents);
    global.NorseShippingThreshold = {
      enabled: data.enabled !== false,
      formatted: data.formatted,
      presentmentCents: data.presentmentCents,
      displayCents: data.displayCents,
      shopCents: data.shopCents,
      shopCurrency: data.shopCurrency,
      cartCurrency: data.cartCurrency,
      metaShopCents: data.metaShopCents,
      formatMoney: formatMoney,
      getPresentmentThresholdCents: getPresentmentThresholdCents,
      getDisplayThresholdCents: displayThresholdCents,
      persist: persistThreshold,
      applyToElement: function (el) {
        applyToElement(el, data.formatted);
      },
    };
    global.NorseShippingThresholdBoot = global.NorseShippingThreshold;
    _boot = global.NorseShippingThreshold;
  }

  function patchBootstrap() {
    var parsed = readBootstrapEl();
    if (!parsed) {
      markReadyIfAppropriate();
      return null;
    }

    var data = parsed.data;
    if (data.enabled === false) {
      publish(data);
      markReady();
      return data;
    }
    var metaShop = parseInt(data.metaShopCents, 10) || 0;
    var shopOverride = readShopOverride(metaShop);

    if (shopOverride > 0 && shopOverride !== data.shopCents) {
      var ratio = data.shopCents > 0 ? data.presentmentCents / data.shopCents : 1;
      if (data.shopCurrency === data.cartCurrency) ratio = 1;
      data.shopCents = shopOverride;
      data.presentmentCents = Math.round(shopOverride * ratio);
      data.displayCents = roundToDisplayCents(data.presentmentCents);
      persistThreshold(data.shopCents, data.presentmentCents);
    }

    data.displayCents = roundToDisplayCents(data.displayCents || data.presentmentCents);
    data.formatted = formatMoney(data.displayCents);
    parsed.el.textContent = JSON.stringify(data);
    publish(data);
    markReadyIfAppropriate();
    return data;
  }

  function syncFromProgressBar() {
    var root = document.querySelector('.free-shipping[data-fs-threshold-presentment]');
    if (!root) return;
    var presentment = parseInt(root.dataset.fsThresholdPresentment, 10);
    var shopThreshold = parseInt(root.dataset.fsThresholdShop, 10);
    if (!(presentment > 0)) return;

    var data = activeData() || readBootstrap() || {};
    if (shopThreshold > 0 && data.shopCents > 0 && shopThreshold !== data.shopCents) return;
    var displayCents = roundToDisplayCents(presentment);
    if (displayCents === data.displayCents && data.formatted) return;

    data.displayCents = displayCents;
    data.presentmentCents = presentment;
    data.formatted = formatMoney(data.displayCents);
    publish(data);
  }

  function thresholdMoney() {
    syncFromProgressBar();
    var data = activeData();
    return (data && data.formatted) || '';
  }

  function getOriginalCopy(node) {
    if (!_originalCopy.has(node)) {
      _originalCopy.set(node, (node.textContent || '').trim());
    }
    return _originalCopy.get(node);
  }

  function applyToNode(node, money) {
    if (!node || !money) return;
    if (node.closest('[data-norse-shipping-threshold]')) return;
    var original = getOriginalCopy(node);
    if (!original) return;
    if (!isFreeShippingText(original) && !containsPlaceholder(original)) return;
    if (!needsClientRewrite(original)) return;
    var updated = replaceInString(original, money);
    if (updated !== node.textContent.trim()) node.textContent = updated;
  }

  function processTree(root) {
    var money = thresholdMoney();
    if (!money || !root) return;
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    var node;
    while ((node = walker.nextNode())) {
      if (!containsPlaceholder(node.nodeValue)) continue;
      node.nodeValue = replaceInString(node.nodeValue, money);
    }
  }

  var MONEY_TOKEN =
    /(?:CA\$|NZ\$|AU\$|HK\$|MX\$|S\$|C\$|A\$|US\$|\$|€|£|¥|₹)[\d.,]+(?:\s+[A-Z]{3})?|(?:RON|SGD|CHF|SEK|NOK|DKK|PLN|CZK|AED|HUF)\s?[\d.,]+(?:\s+lei)?|[\d.,]+\s*(?:lei|€|£|kr)\b/gi;

  function stripIsoSuffixes(str) {
    return String(str || '')
      .replace(/(A\$[\d.,]+)\s+AUD\b/g, '$1')
      .replace(/(CA\$[\d.,]+)\s+CAD\b/g, '$1')
      .replace(/(NZ\$[\d.,]+)\s+NZD\b/g, '$1')
      .replace(/(HK\$[\d.,]+)\s+HKD\b/g, '$1')
      .replace(/(S\$[\d.,]+)\s+SGD\b/g, '$1')
      .replace(/(MX\$[\d.,]+)\s+MXN\b/g, '$1')
      .replace(/(\$[\d.,]+)\s+USD\b/g, '$1')
      .replace(/(€[\d.,]+)\s+EUR\b/g, '$1')
      .replace(/(£[\d.,]+)\s+GBP\b/g, '$1');
  }

  function normalizeThresholdCopy(str, money) {
    if (!str || !money) return str;
    if (str.indexOf(money) !== -1) return stripIsoSuffixes(str);
    var out = replaceInString(str, money);
    if (out !== str) return stripIsoSuffixes(out);
    if (!isFreeShippingText(str)) return str;
    return stripIsoSuffixes(str.replace(MONEY_TOKEN, money));
  }

  function processServerThresholdNodes() {
    var money = thresholdMoney();
    if (!money) return;

    document.querySelectorAll('[data-norse-shipping-threshold]').forEach(function (el) {
      if (el.children.length === 1 && el.firstElementChild && el.firstElementChild.tagName === 'P') {
        var p = el.firstElementChild;
        var updated = normalizeThresholdCopy((p.textContent || '').trim(), money);
        if (updated && updated !== (p.textContent || '').trim()) p.textContent = updated;
        return;
      }
      var text = (el.textContent || '').trim();
      var updated = normalizeThresholdCopy(text, money);
      if (updated && updated !== text) el.textContent = updated;
    });
  }

  function processHardcodedSelectors() {
    var money = thresholdMoney();
    if (!money) return;

    HARDCODED_SELECTORS.forEach(function (selector) {
      document.querySelectorAll(selector).forEach(function (el) {
        applyToNode(el, money);
      });
    });

    document.querySelectorAll('.norse-usps-small--threshold-pending').forEach(function (el) {
      if (!canRevealPending()) return;
      applyToElement(el, money);
      if (el.dataset) el.dataset.freeShippingThreshold = String((activeData() || {}).displayCents || '');
      el.classList.remove('norse-usps-small--threshold-pending');
    });
  }

  // Liquid's `t` filter already HTML-escapes non-`_html` keys, so a template read
  // back out of an attribute can still carry entities (e.g. "SUBSCRIBE &amp; SAVE").
  // Writing that straight into textContent renders the entity literally, so decode first.
  var _entityDecoder = null;
  function decodeEntities(str) {
    if (!str || str.indexOf('&') === -1) return str;
    if (!_entityDecoder) _entityDecoder = document.createElement('textarea');
    _entityDecoder.innerHTML = str;
    return _entityDecoder.value || str;
  }

  function formatMatchCartMoney() {
    document.querySelectorAll('[data-norse-money-cents]').forEach(function (el) {
      var cents = parseInt(el.getAttribute('data-norse-money-cents'), 10);
      if (!(cents >= 0)) return;
      var money = formatMoney(cents);
      if (!money) return;
      var tmpl = decodeEntities(el.getAttribute('data-norse-money-from-template'));
      var next = money;
      if (tmpl && tmpl.indexOf('__AMOUNT__') !== -1) {
        next = tmpl.replace(/__AMOUNT__/g, money);
      }
      if ((el.textContent || '').trim() !== next) el.textContent = next;
    });
  }

  var _matchCartMoneyTimer = null;
  function scheduleFormatMatchCartMoney() {
    if (_matchCartMoneyTimer) return;
    _matchCartMoneyTimer = global.setTimeout(function () {
      _matchCartMoneyTimer = null;
      formatMatchCartMoney();
    }, 50);
  }

  function observeMatchCartMoney() {
    if (global.__NORSE_MATCH_CART_MONEY_OBS__ || !document.body) return;
    global.__NORSE_MATCH_CART_MONEY_OBS__ = true;
    new MutationObserver(scheduleFormatMatchCartMoney).observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  function run() {
    if (!thresholdEnabled()) {
      hideFreeShippingCopy();
      formatMatchCartMoney();
      markReady();
      return;
    }
    processTree(document.body);
    processServerThresholdNodes();
    processHardcodedSelectors();
    formatMatchCartMoney();
    markReadyIfAppropriate();
  }

  patchBootstrap();

  function refreshFormattedWhenReady() {
    if (!global.Shopify || typeof global.Shopify.formatMoney !== 'function') return;
    var data = activeData() || readBootstrap();
    if (!data || !(data.presentmentCents > 0)) return;
    data.displayCents = roundToDisplayCents(data.displayCents || data.presentmentCents);
    var next = formatMoney(data.displayCents);
    if (next && next !== data.formatted) {
      data.formatted = next;
      publish(data);
      run();
    }
  }

  document.addEventListener('norse:fs-threshold-update', function (event) {
    var detail = (event && event.detail) || {};
    if (detail.shopCents) global.__NORSE_FS_SHOP_CENTS = String(detail.shopCents);
    patchBootstrap();
    run();
  });

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      function () {
        refreshFormattedWhenReady();
        scheduleCartThresholdSync();
        observeMatchCartMoney();
        run();
      },
      { once: true }
    );
  } else {
    refreshFormattedWhenReady();
    scheduleCartThresholdSync();
    observeMatchCartMoney();
    run();
  }

  document.addEventListener('cart:refresh', function () {
    syncFromCartAttributes().finally(run);
  });
  document.addEventListener('cart:update', function () {
    syncFromCartAttributes().finally(run);
  });

  global.NorseShippingThresholdContent = { run: run, thresholdMoney: thresholdMoney };
})(window);
